import './CSPOutageMap.scss';

import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import LeafletMap from '../../../Monitor/components/LeafletMap';
import { LeafletMapMarkerGroup } from '../../../Monitor/components/LeafletMap/types';
import useAutonomousMonitors from '../../../Monitor/hooks/useAutonomousMonitors';
import { AutonomousMonitorType } from '../../../Monitor/types';
import { getAutonomousMonitorLocations } from '../../../Monitor/services/monitorQueries';
import { getFrontlineUrl } from '../../../../utils/environments';
import { Icons, PendingContent, Spinner, Tooltip } from 'plume-ui';
import ErrorBlock from '../../../../components/ErrorBlock/ErrorBlock';
import { useTranslation } from 'react-i18next';
import { getColorsForGraph } from '../../../Monitor/components/AutonomousMonitorCell/helpers';

const CSPOutageMap = () => {
  const { t } = useTranslation();
  const {
    data: autonomousMonitors,
    isLoading: loadingAutoMonitors,
  } = useAutonomousMonitors();

  const cspOutageMonitor = useMemo(() => {
    return autonomousMonitors?.find(
      (monitor) => monitor.monitorType === AutonomousMonitorType.CspOutage,
    );
  }, [autonomousMonitors]);

  const {
    data: locationData,
    loading: loadingLocations,
    error: locationError,
  } = useQuery(getAutonomousMonitorLocations(cspOutageMonitor?.id || ''), {
    skip: !cspOutageMonitor?.id,
  });

  const onClickMarker = (locationId: string) => {
    window.open(`${getFrontlineUrl()}/location/${locationId}`, '_blank');
  };

  const markerGroups: LeafletMapMarkerGroup[] = useMemo(() => {
    if (!locationData) return [];
    return [
      {
        color: '#ffc501', // Warning (yellow) base color
        heatmap: true,
        heatmapConfig: {
          minOpacity: 0.3,
          gradient: {
            0.0: 'rgba(255, 239, 186, 0)',
            0.2: 'rgba(255, 223, 128, 0.4)',
            0.4: 'rgba(255, 211, 77, 0.6)',
            0.6: 'rgba(255, 197, 1, 0.8)',
            0.8: 'rgba(255, 180, 0, 0.9)',
            1.0: 'rgb(255, 165, 0)',
          },
        },
        markers: (locationData.locations.yellow ?? []).map((location) => ({
          lat: location.latitude || 0,
          long: location.longitude || 0,
          tooltip: location.location_id,
          onClick: () => onClickMarker(location.location_id),
        })),
      },
      {
        color: '#dc0462', // Alert (red) base color
        heatmap: true,
        heatmapConfig: {
          minOpacity: 0.3,
          gradient: {
            0.0: 'rgba(255, 214, 220, 0)',
            0.2: 'rgba(255, 174, 186, 0.4)',
            0.4: 'rgba(255, 123, 133, 0.6)',
            0.6: 'rgba(255, 71, 120, 0.8)',
            0.8: 'rgba(220, 4, 98, 0.9)',
            1.0: 'rgb(180, 0, 60)',
          },
        },
        markers: (locationData.locations.red ?? []).map((location) => ({
          lat: location.latitude || 0,
          long: location.longitude || 0,
          tooltip: location.location_id,
          onClick: () => onClickMarker(location.location_id),
        })),
      },
    ];
  }, [locationData]);

  const totalMarkers =
    (locationData?.locations.red?.length || 0) +
    (locationData?.locations.yellow?.length || 0);

  const getRangeForSection = (section: string) => {
    if (!cspOutageMonitor) return '';
    if (section === 'High-Normal')
      return `${cspOutageMonitor.yellow.range.durationInMinsLowerBound} - ${cspOutageMonitor.yellow.range.durationInMinsUpperBound}`;
    if (section === 'Outlier')
      return `${cspOutageMonitor.red.range.durationInMinsLowerBound}+`;
    return `${cspOutageMonitor.green.range.durationInMinsLowerBound} - ${cspOutageMonitor.green.range.durationInMinsUpperBound}`;
  };

  if (loadingLocations) {
    return (
      <PendingContent loading loader={Spinner}>
        <div style={{ height: 500 }}></div>
      </PendingContent>
    );
  }

  if (locationError) {
    return <ErrorBlock solid height={500} />;
  }

  return (
    <div className="CSPOutageMap">
      <div className="CSPOutageMap__title">
        <span>{t('monitors.criteriaNames.csp_outage')}</span>
        {cspOutageMonitor && (
          <Tooltip
            subSectionsObj={[
              {
                subheading: `${t('monitors.autonomous.typicalRange')}:`,
                paragraph: '',
              },
            ]}
            dataObjs={[
              {
                key: t('monitors.autonomous.ranges.normal', {
                  range: getRangeForSection('Normal'),
                }),
                value: '',
                color: getColorsForGraph('Normal').latest,
              },
              {
                key: t('monitors.autonomous.ranges.highNormal', {
                  range: getRangeForSection('High-Normal'),
                }),
                value: '',
                color: getColorsForGraph('High-Normal').latest,
              },
              {
                key: t('monitors.autonomous.ranges.outlier', {
                  range: getRangeForSection('Outlier'),
                }),
                value: '',
                color: getColorsForGraph('Outlier').latest,
              },
            ]}
            openInPortal
            type="hoverType"
            position="right"
          >
            <Icons.InfoIcon width={16} />
          </Tooltip>
        )}
      </div>
      <div className="CSPOutageMap__mapContainer">
        <LeafletMap
          markerCount={totalMarkers}
          markerGroups={markerGroups}
          height={500}
          zoomControl
          zoom={2}
          showHeatmapAtZoom={[0, 4]}
          isLoading={loadingLocations}
          showMarkersAtZoom={5}
          withCard={false}
        />

        {!loadingAutoMonitors && (autonomousMonitors?.length ?? 0) === 0 && (
          <div className="CSPOutageMap__noData">
            <Icons.NetworkIcon width={32} height={32} />
            {t('healthSummary.cspOutageMap.contactPlumeRep')}
          </div>
        )}
        {!loadingLocations &&
          totalMarkers === 0 &&
          (autonomousMonitors?.length ?? 0) > 0 && (
            <div className="CSPOutageMap__noData">
              <Icons.NetworkIcon width={32} height={32} />
              {t('healthSummary.cspOutageMap.noOutages')}
            </div>
          )}
      </div>
    </div>
  );
};

export default CSPOutageMap;
